import styles from "./ArticleLeadArt.module.scss";
import { ArticleLayout, ArticleLeadArtSwanFragment } from "types/api";
import ArticleLeadFigure from "components/article/ArticleLeadFigure";
import Image from "components/Image";
import Cinemagraph from "components/Cinemagraph";
import IFrame from "components/IFrame";
import { gql } from "utils/urql";
import cx from "classnames";

const dimensions = {
  standard: {
    width: 976,
    height: 549,
  },
  feature: {
    width: 1920,
    height: 1080,
  },
};

const ArticleLeadArt = ({
  layout,
  ...props
}: { layout: ArticleLayout } & ArticleLeadArtSwanFragment) => {
  return (
    <div className={cx(styles.root, layout === "feature" && styles.feature)}>
      {(props.__typename === "LeadArtImage" ||
        props.__typename === "LeadArtImageLarge") &&
        props.image && (
          <ArticleLeadFigure layout={layout} {...props.image}>
            <Image
              className={cx(
                styles.image,
                layout === "feature" && styles.featureMedia
              )}
              src={props.image.url}
              srcSet={props.image.srcSet || undefined}
              width={props.image.width}
              height={props.image.height}
              alt={props.image.altText || ""}
              lazy={false}
              preload={true}
              /**
               * min-width's are breakpoints, starting with mobile first
               * 100vw tells browsers the image is fluid below $breakpoint-article-nav
               * Above $breakpoint-article-nav, art is fixed at 976px
               */
              sizes={
                layout === "standard"
                  ? [
                      "100vw",
                      `(min-width: ${dimensions.standard.width}px) ${dimensions.standard.width}px`,
                    ].join(", ")
                  : [
                      "100vw",
                      `(min-width: ${dimensions.feature.width}px) ${dimensions.feature.width}px`,
                    ].join(", ")
              }
            />
          </ArticleLeadFigure>
        )}
      {props.__typename === "Cinemagraph" && (
        <ArticleLeadFigure
          layout={layout}
          captionText={props.captionText}
          attributionText={props.attributionText}
        >
          <Cinemagraph
            width={props.poster?.width || dimensions.feature.width}
            height={props.poster?.height || dimensions.feature.height}
            poster={props.poster || undefined}
            formats={props.formats}
            className={cx(
              styles.video,
              layout === "feature" && styles.featureMedia
            )}
          />
        </ArticleLeadFigure>
      )}
      {props.__typename === "LeadArtEmbed" && (
        <ArticleLeadFigure layout={layout} captionText={props.caption}>
          <IFrame
            className={cx(
              styles.embed,
              layout === "feature" && styles.featureMedia
            )}
            src={props.src}
            width={props.width}
            height={props.height}
            title="Embedded interactive content"
          />
        </ArticleLeadFigure>
      )}
    </div>
  );
};

ArticleLeadArt.fragment = gql`
  fragment articleLeadArtSwan on LeadArt {
    ... on LeadArtImage {
      image(
        width: 976
        height: 549
        # 750w = 375px * 2 (mobile retina)
        # 828w (mobile large retina)
        # 976w (default size for desktop & desktop large)
        # 1952w = 976 * 2 (desktop retina)
        sizes: ["750w", "828w", "976w", "1952w"]
      ) {
        url
        width
        height
        srcSet
        captionText
        attributionText
        attributionUrl
        altText
      }
    }
    ... on LeadArtImageLarge {
      image(
        # fix aspect ratio, but let srcset & sizes load larger if possible
        width: 1440
        height: 810
        # Sizes based off of default inspector responsive sizes
        # 640 = 320px * 2 (mobile small retina)
        # 750w = 375 * 2 (mobile retina)
        # 850w = 425 * 2 (mobile large retina)
        # 1536w = 768 * 2 (tablet retina)
        # 1920w = (standard lead art large size)
        # 2048w = 1024 * 2 (laptop retina)
        # 2880w = 1440 * 2 (laptop large retina)
        # "3840w" = 1920 * 2 (standard lead art retina)
        sizes: [
          "640w"
          "750w"
          "850w"
          "1536w"
          "1920w"
          "2048w"
          "2880w"
          "3840w"
        ]
      ) {
        url
        width
        height
        srcSet
        captionText
        attributionText
        attributionUrl
        altText
      }
    }
    ... on Cinemagraph {
      attributionText
      captionText
      formats {
        encoding
        url
      }
      poster(width: 1920, height: 1080) {
        width
        height
        url
      }
    }
    ... on LeadArtEmbed {
      src
      width
      height
      caption
    }
  }
`;

export default ArticleLeadArt;
